/**
 * ScheduledReportsPage — V2 CRUD table for managing scheduled reports.
 *
 * Displays all scheduled reports for a campus with search, sort, column filters,
 * pagination, bulk actions, and kebab menu (Edit, Send, Delete).
 * Edit drawer includes frequency/day/time/attachments/email configuration.
 * Dynamic filter drawer changes sections based on report type.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession, getCampusId, getUserId, getSessionId, getCampusList,
    updateSessionCampus,
} from '../../../services/session.js';
import { iAmHigherAuthority, calculateSendTime, convertHHMMtoSec } from '../../../libs.jsx';
import { exportWellnessCsv } from '../../../services/csvExport.js';
import {
    fetchSchedules, fetchFrequencies, fetchScheduleFilterOptions,
    editSchedule, deleteSchedules, sendScheduleReport,
} from './api/scheduledApi.js';
// fetchUsers returns user_role_weight for each user — needed for the User
// Recipients hierarchy gate (no add/remove of users with a higher weight).
import { fetchUsers } from '../../users/api/usersApi.js';

import ScheduledTable, { COLUMN_DEFS } from './components/ScheduledTable.jsx';
import ScheduleDrawer from './components/ScheduleDrawer.jsx';
import ScheduleFilterDrawer, { serializeFilters } from './components/ScheduleFilterDrawer.jsx';

import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';

import './scheduled-reports-page.css';

const ScheduledReportsPage = () => {
    // ======================================================================
    // SESSION
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);
    const tzOffset = campusConfig?.tz_offset;
    const roleWeight = session?.my_role_weight || 10;
    const editable = iAmHigherAuthority(10, roleWeight);
    // Edit (other than User Recipients) and Delete require role weight > 20.
    // Users below this can still open the edit drawer and modify the User
    // Recipients list (subject to per-user role hierarchy).
    const canManageReports = useMemo(() => iAmHigherAuthority(20, roleWeight), [roleWeight]);

    const getSessionParams = useCallback(() => ({
        campusId,
        userId: getUserId(),
        clientId: getSessionId(),
    }), [campusId]);

    // ======================================================================
    // DATA STATE
    // ======================================================================
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [frequencies, setFrequencies] = useState([]);
    const [filterOptions, setFilterOptions] = useState({});
    // Users with their user_role_weight populated, for the User Recipients
    // hierarchy gate. Sourced from sp_dev_getgroupsforusersbycampusid via the
    // users module (portalFilters.users does not include role weight).
    const [usersWithWeight, setUsersWithWeight] = useState([]);

    // ======================================================================
    // TABLE STATE
    // ======================================================================
    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sortColumn, setSortColumn] = useState('description');
    const [sortOrder, setSortOrder] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();
    const [selectedIds, setSelectedIds] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState(() => COLUMN_DEFS.map((c) => c.key));

    // ======================================================================
    // DRAWER STATE
    // ======================================================================
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    // ======================================================================
    // CONFIRM DIALOG STATE
    // ======================================================================
    const [confirmAction, setConfirmAction] = useState(null); // { type, target, message }

    // ======================================================================
    // TOAST
    // ======================================================================
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);

    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 4000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // ======================================================================
    // DATA LOADING
    // ======================================================================
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [data, freqs, opts, usersData] = await Promise.all([
                fetchSchedules(campusId),
                fetchFrequencies(),
                fetchScheduleFilterOptions(campusId),
                fetchUsers(campusId),
            ]);
            setSchedules(Array.isArray(data) ? data : []);
            setFrequencies(Array.isArray(freqs) ? freqs : []);
            setFilterOptions(opts || {});
            setUsersWithWeight(Array.isArray(usersData) ? usersData : []);
        } catch (err) {
            console.error('[ScheduledReportsPage] Load error:', err);
            showToast('error', 'Failed to load scheduled reports');
        } finally {
            setLoading(false);
        }
    }, [campusId, showToast]);

    useEffect(() => { loadData(); }, [loadData]);

    // Map of user_id -> user_role_weight. Used to gate User Recipient
    // adds/removes by role hierarchy.
    const userWeightById = useMemo(() => {
        const map = {};
        (usersWithWeight || []).forEach((u) => {
            if (u?.user_id != null) map[String(u.user_id)] = u.user_role_weight;
        });
        return map;
    }, [usersWithWeight]);

    // filterOptions.users does not include user_role_weight. Merge it in so the
    // ScheduleDrawer's User Recipients list can be hierarchy-gated.
    const filterOptionsWithWeights = useMemo(() => {
        if (!filterOptions?.users) return filterOptions;
        return {
            ...filterOptions,
            users: filterOptions.users.map((u) => ({
                ...u,
                user_role_weight: userWeightById[String(u.user_id)] ?? u.user_role_weight,
            })),
        };
    }, [filterOptions, userWeightById]);

    // ======================================================================
    // CAMPUS CHANGE
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setSelectedIds([]);
    }, []);

    // ======================================================================
    // DATA PIPELINE: search → column filters → sort → paginate
    // ======================================================================
    const searchedData = useMemo(() => {
        if (!search) return schedules;
        const term = search.toLowerCase();
        return schedules.filter((row) =>
            (row.description || '').toLowerCase().includes(term) ||
            (row.report_type_name || '').toLowerCase().includes(term) ||
            (row.frequency_name || '').toLowerCase().includes(term) ||
            (row.report_receiver || '').toLowerCase().includes(term)
        );
    }, [schedules, search]);

    const columnFilteredData = useMemo(() => {
        const active = Object.entries(columnFilters).filter(([, vals]) => vals && vals.length > 0);
        if (active.length === 0) return searchedData;
        return searchedData.filter((row) =>
            active.every(([key, vals]) => vals.includes(row[key] != null ? String(row[key]) : ''))
        );
    }, [searchedData, columnFilters]);

    const sortedData = useMemo(() => {
        if (!sortColumn) return columnFilteredData;
        return [...columnFilteredData].sort((a, b) => {
            let aVal = a[sortColumn];
            let bVal = b[sortColumn];
            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';
            if (sortColumn === 'send_report_time') {
                aVal = parseInt(aVal, 10) || 0;
                bVal = parseInt(bVal, 10) || 0;
                return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
            return sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [columnFilteredData, sortColumn, sortOrder]);

    const totalFiltered = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const paginatedData = useMemo(() => {
        const startIdx = (safePage - 1) * perPage;
        return sortedData.slice(startIdx, startIdx + perPage);
    }, [sortedData, safePage, perPage]);
    const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
    const showingEnd = Math.min(safePage * perPage, totalFiltered);

    const pageNumbers = useMemo(() => {
        const pages = [];
        const max = 5;
        let startP = Math.max(1, safePage - Math.floor(max / 2));
        let endP = Math.min(totalPages, startP + max - 1);
        if (endP - startP < max - 1) startP = Math.max(1, endP - max + 1);
        for (let i = startP; i <= endP; i++) pages.push(i);
        return pages;
    }, [safePage, totalPages]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const handleSort = useCallback((colKey) => {
        setSortColumn((prev) => {
            if (prev === colKey) { setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc')); return prev; }
            setSortOrder('asc');
            return colKey;
        });
        setPage(1);
    }, []);

    const handleColumnFilterChange = useCallback((columnKey, selectedValues) => {
        setColumnFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
        setPage(1);
    }, []);

    const handleClearColumnFilters = useCallback(() => { setColumnFilters({}); setPage(1); }, []);

    const handleSelectRow = useCallback((id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }, []);

    const handleSelectAll = useCallback(() => {
        const pageIds = paginatedData.map((r) => r.report_id);
        const allSelected = pageIds.every((id) => selectedIds.includes(id));
        if (allSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
        }
    }, [paginatedData, selectedIds]);

    // Edit
    const handleEdit = useCallback((row) => {
        setEditingSchedule(row);
        setEditDrawerOpen(true);
    }, []);

    const handleSave = useCallback(async (formData) => {
        setSaving(true);
        const sessionParams = getSessionParams();

        // Defense-in-depth, applied to every save:
        //  1. Re-validate the report_user_ids diff against role hierarchy —
        //     additions and removals of users with a higher role weight than
        //     the current user are silently dropped.
        //  2. If the user lacks canManageReports, every field other than
        //     report_user_ids is overridden with the original schedule value
        //     so a tampered drawer state cannot mutate them.
        const original = schedules.find((s) => s.report_id === formData.report_id);
        if (!original) {
            setSaving(false);
            showToast('error', 'Failed to save schedule');
            return;
        }
        const origIds = new Set(
            String(original.report_user_ids || '').split(',').map((id) => id.trim()).filter(Boolean)
        );
        const requestedIds = new Set(
            String(formData.report_user_ids || '').split(',').map((id) => id.trim()).filter(Boolean)
        );
        const finalIds = new Set(origIds);
        requestedIds.forEach((id) => {
            if (origIds.has(id)) return;
            if (iAmHigherAuthority(userWeightById[id], roleWeight)) finalIds.add(id);
        });
        origIds.forEach((id) => {
            if (requestedIds.has(id)) return;
            if (iAmHigherAuthority(userWeightById[id], roleWeight)) finalIds.delete(id);
        });
        const sanitizedUserIds = [...finalIds].join(',');

        const safeForm = canManageReports
            ? { ...formData, report_user_ids: sanitizedUserIds }
            : { ...original, report_user_ids: sanitizedUserIds };

        // schedule_edit expects report_filter_terms as a plain object — the
        // transaction layer JSON-encodes it once for storage. Schedules loaded
        // from the DB hand us the value as a JSON string; normalize before
        // sending so an unchanged save round-trips cleanly.
        const normalizedFilterTerms = (() => {
            const v = safeForm.report_filter_terms;
            if (v == null || v === '') return {};
            if (typeof v === 'string') {
                try { return JSON.parse(v); } catch { return {}; }
            }
            return v;
        })();

        const payload = {
            ...safeForm,
            report_filter_terms: normalizedFilterTerms,
            next_send_time: calculateSendTime(
                safeForm.issue_freq_id,
                String(safeForm.window_frequency_reference || '').split(',').filter(Boolean),
                secondsToTimeStr(safeForm.send_report_time),
                parseInt(tzOffset || 0, 10)
            ),
        };

        const result = await editSchedule(payload, { report_id: safeForm.report_id }, sessionParams);
        setSaving(false);

        if (result[0]) {
            showToast('success', `"${safeForm.description}" updated`);
            setEditDrawerOpen(false);
            setEditingSchedule(null);
            loadData();
        } else {
            showToast('error', result[1] || 'Failed to save schedule');
        }
    }, [getSessionParams, tzOffset, loadData, showToast, canManageReports, schedules, userWeightById, roleWeight]);

    // Send
    const handleSend = useCallback((row) => {
        setConfirmAction({ type: 'send', target: row, message: `Send "${row.description}" now?` });
    }, []);

    const handleConfirmSend = useCallback(async () => {
        const row = confirmAction?.target;
        if (!row) return;
        setConfirmAction(null);

        const result = await sendScheduleReport(row, campusId, getSessionId());
        if (result[0]) {
            showToast('success', `"${row.description}" sent successfully`);
        } else {
            showToast('error', result[1] || 'Failed to send report');
        }
    }, [confirmAction, campusId, showToast]);

    // Delete
    const handleDelete = useCallback((row) => {
        if (!canManageReports) {
            showToast('error', 'You do not have permission to perform this action.');
            return;
        }
        setConfirmAction({ type: 'delete', target: [row], message: `Delete "${row.description}"?` });
    }, [canManageReports, showToast]);

    const handleBulkDelete = useCallback(() => {
        if (!canManageReports) {
            showToast('error', 'You do not have permission to perform this action.');
            return;
        }
        const items = schedules.filter((s) => selectedIds.includes(s.report_id));
        if (items.length === 0) return;
        setConfirmAction({ type: 'delete', target: items, message: `Delete ${items.length} scheduled report${items.length > 1 ? 's' : ''}?` });
    }, [schedules, selectedIds, canManageReports, showToast]);

    const handleConfirmDelete = useCallback(async () => {
        const items = confirmAction?.target;
        if (!items) return;
        setConfirmAction(null);
        if (!canManageReports) {
            showToast('error', 'You do not have permission to perform this action.');
            return;
        }

        const sessionParams = getSessionParams();
        const result = await deleteSchedules(items, sessionParams);
        if (result[0]) {
            showToast('success', `${items.length} report${items.length > 1 ? 's' : ''} deleted`);
            setSelectedIds([]);
            loadData();
        } else {
            showToast('error', result[1] || 'Failed to delete');
        }
    }, [confirmAction, getSessionParams, loadData, showToast, canManageReports]);

    // Filter drawer
    const handleOpenFilterDrawer = useCallback(() => {
        setFilterDrawerOpen(true);
    }, []);

    // Store as a plain object — the schedule_edit transaction JSON-encodes the
    // value once when persisting. Pre-stringifying here would double-encode it
    // and break downstream consumers (e.g. the lambda report builder) that do
    // a single json.loads on the stored value.
    const handleApplyFilters = useCallback((serializedFilters) => {
        if (editingSchedule) {
            setEditingSchedule((prev) => ({
                ...prev,
                report_filter_terms: serializedFilters,
            }));
        }
        setFilterDrawerOpen(false);
    }, [editingSchedule]);

    // Export
    const handleExportCsv = useCallback(() => {
        exportWellnessCsv(sortedData, COLUMN_DEFS, 'scheduled-reports.csv');
    }, [sortedData]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ar-page">
                    {/* Toast */}
                    {toast ? (
                        <div className={`ap-toast ap-toast--${toast.type}`} role="status" aria-live="polite">
                            {toast.type === 'success' ? (
                                <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            ) : (
                                <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
                            )}
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="ap-toast__close" aria-label="Dismiss">{'\u2715'}</button>
                        </div>
                    ) : null}

                    {/* PAGE HEADER */}
                    <div className="ar-page-header">
                        <div className="ar-page-header__inner">
                            <div className="ar-breadcrumb-row">
                                <div className="ar-breadcrumb">
                                    <span className="ar-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ar-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ar-breadcrumb__current">Scheduled</span>
                                </div>
                                <div className="ar-campus-row">
                                    <label htmlFor="sr-campus-selector" className="ar-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="sr-campus-selector" value={campusId} onChange={handleCampusChange} className="ar-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ar-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>
                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ar-page-title">Scheduled Reports</h1>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MAIN CONTENT */}
                    <div className="ar-content">

                        {/* Controls row */}
                        <div className="ap-controls">
                            <div className="ap-controls__left">
                                <div className="ap-search">
                                    <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                    </svg>
                                    <input
                                        type="search"
                                        placeholder="Search schedules..."
                                        value={search}
                                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                        className="ap-search__input"
                                        aria-label="Search schedules"
                                    />
                                </div>
                            </div>
                            <div className="ap-controls__right">
                                <button className="rp-btn rp-btn--secondary" onClick={handleExportCsv}>
                                    <i className="fe fe-download" style={{ marginRight: '4px' }} />CSV
                                </button>
                            </div>
                        </div>

                        {/* Bulk action bar — below controls, above table (matches AreasPage) */}
                        {selectedIds.length > 0 ? (
                            <div className="ap-bulk-bar">
                                <div className="ap-bulk-bar__left">
                                    <span className="ap-bulk-bar__count">
                                        {selectedIds.length} schedule{selectedIds.length !== 1 ? 's' : ''} selected
                                    </span>
                                    <button type="button" onClick={() => setSelectedIds([])} className="ap-bulk-bar__deselect">
                                        Deselect All
                                    </button>
                                </div>
                                <div className="ap-bulk-bar__right">
                                    <button type="button" onClick={handleExportCsv} className="ap-bulk-btn" aria-label="Export selected schedules">
                                        <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                        </svg>
                                        Export
                                    </button>
                                    {canManageReports ? (
                                        <button type="button" onClick={handleBulkDelete} className="ap-bulk-btn ap-bulk-btn--danger" aria-label="Delete selected schedules">
                                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                            </svg>
                                            Delete
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        {/* Loading skeleton */}
                        {loading ? (
                            <div className="ar-loading">
                                <div className="ar-loading__spinner" />
                                <p>Loading scheduled reports...</p>
                            </div>
                        ) : null}

                        {/* Table */}
                        {!loading ? (
                            <ScheduledTable
                                data={paginatedData}
                                allData={sortedData}
                                visibleColumns={visibleColumns}
                                sortColumn={sortColumn}
                                sortOrder={sortOrder}
                                onSort={handleSort}
                                filters={columnFilters}
                                onFilterChange={handleColumnFilterChange}
                                onClearFilters={handleClearColumnFilters}
                                selectedIds={selectedIds}
                                onSelectRow={handleSelectRow}
                                onSelectAll={handleSelectAll}
                                onEdit={handleEdit}
                                onSend={handleSend}
                                onDelete={handleDelete}
                                tzOffset={tzOffset}
                                users={filterOptions?.users || []}
                                canManageReports={canManageReports}
                            />
                        ) : null}

                        {/* Pagination */}
                        {!loading && totalFiltered > 0 ? (
                            <div className="ap-pagination">
                                <p className="ap-pagination__info">
                                    Showing <span className="ap-pagination__emphasis">{showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{showingEnd}</span> of{' '}
                                    <span className="ap-pagination__emphasis">{totalFiltered}</span> schedules
                                </p>
                                <div className="ap-pagination__controls">
                                    <div className="ap-per-page">
                                        <label htmlFor="sr-per-page" className="ap-per-page__label">Rows per page:</label>
                                        <select id="sr-per-page" value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }} className="ap-per-page__select">
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                            <option value={300}>300</option>
                                        </select>
                                    </div>
                                    <nav className="ap-page-nav" aria-label="Schedules pagination">
                                        <button type="button" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} className="ap-page-btn" aria-label="Previous page">
                                            <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                                        </button>
                                        {pageNumbers.map((num) => (
                                            <button key={num} type="button" onClick={() => setPage(num)} className={`ap-page-num${num === safePage ? ' is-active' : ''}`} aria-label={`Page ${num}`} aria-current={num === safePage ? 'page' : undefined}>{num}</button>
                                        ))}
                                        <button type="button" onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage >= totalPages} className="ap-page-btn" aria-label="Next page">
                                            <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Edit Drawer */}
                    <ScheduleDrawer
                        open={editDrawerOpen}
                        onClose={() => { setEditDrawerOpen(false); setEditingSchedule(null); }}
                        onSave={handleSave}
                        schedule={editingSchedule}
                        frequencies={frequencies}
                        onEditFilters={handleOpenFilterDrawer}
                        saving={saving}
                        filterOptions={filterOptionsWithWeights}
                        canManageReport={canManageReports}
                        myRoleWeight={roleWeight}
                    />

                    {/* Dynamic Filter Drawer */}
                    <ScheduleFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        reportTypeId={editingSchedule?.report_type_id}
                        filterOptions={filterOptions}
                        currentFilterTerms={editingSchedule?.report_filter_terms}
                    />

                    {/* Confirmation Dialog */}
                    {confirmAction ? (
                        <div className="sr-confirm-backdrop" onClick={() => setConfirmAction(null)}>
                            <div className="sr-confirm-dialog" onClick={(e) => e.stopPropagation()}>
                                <h3 className="sr-confirm-dialog__title">
                                    {confirmAction.type === 'send' ? 'Send Report' : 'Delete Report'}
                                </h3>
                                <p className="sr-confirm-dialog__text">{confirmAction.message}</p>
                                <div className="sr-confirm-dialog__actions">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setConfirmAction(null)}>Cancel</button>
                                    {confirmAction.type === 'send' ? (
                                        <button className="dp-btn dp-btn--primary" onClick={handleConfirmSend}>Send Now</button>
                                    ) : (
                                        <button className="dp-btn dp-btn--danger" onClick={handleConfirmDelete}>Delete</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

/** Convert seconds to HH:MM string for calculateSendTime */
function secondsToTimeStr(sec) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default ScheduledReportsPage;
